import type { DashboardMapPoint } from '@/types/dashboard';
import { resolveIndiaCoords } from '@/utils/indiaCityCoords';

export interface MapMarker extends DashboardMapPoint {
  lat: number;
  lng: number;
}

export interface SalesByCityItem {
  label: string;
  value: number;
  count: number;
}

export function getMapMarkerKey(marker: Pick<DashboardMapPoint, 'city' | 'state'>): string {
  return `${marker.city}::${marker.state}`;
}

export function buildMapMarkers(
  points: DashboardMapPoint[],
  salesByCity: SalesByCityItem[] = [],
): MapMarker[] {
  const source =
    points.length > 0
      ? points
      : salesByCity.map((item) => ({
          city: item.label,
          state: '',
          orders: item.count,
          revenue: item.value,
        }));

  return source
    .map((point): MapMarker | null => {
      const coords = resolveIndiaCoords(point.city, point.state);
      if (!coords) return null;
      return { ...point, ...coords };
    })
    .filter((point): point is MapMarker => point !== null)
    .sort((a, b) => b.revenue - a.revenue || b.orders - a.orders);
}

export function createMapPinIcon(count: number, isActive: boolean): google.maps.Icon {
  const fill = isActive ? '#0D4078' : '#115099';
  const ring = isActive ? '#d4b970' : '#ffffff';
  const width = isActive ? 46 : 40;
  const height = Math.round(width * 1.2);
  const shadowId = `pin-shadow-${count}-${isActive ? 'a' : 'b'}`;

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 40 48">
    <defs>
      <filter id="${shadowId}" x="-20%" y="-10%" width="140%" height="140%">
        <feDropShadow dx="0" dy="2" stdDeviation="2.5" flood-color="#0e0e13" flood-opacity="0.22"/>
      </filter>
    </defs>
    <path filter="url(#${shadowId})" fill="${fill}" stroke="${ring}" stroke-width="${isActive ? 2.5 : 2}"
      d="M20 1C11.2 1 4 8.2 4 17c0 11.2 16 28 16 28s16-16.8 16-28C36 8.2 28.8 1 20 1z"/>
    <circle cx="20" cy="16" r="10" fill="#ffffff"/>
    <text x="20" y="20" text-anchor="middle" font-family="system-ui,-apple-system,sans-serif"
      font-size="11" font-weight="700" fill="${fill}">${count}</text>
  </svg>`;

  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: new google.maps.Size(width, height),
    anchor: new google.maps.Point(width / 2, height),
  };
}

export function sumMarkerOrders(markers: MapMarker[]): number {
  return markers.reduce((sum, marker) => sum + marker.orders, 0);
}

export function sumMarkerRevenue(markers: MapMarker[]): number {
  return markers.reduce((sum, marker) => sum + marker.revenue, 0);
}
